import { db } from "./firebase";
import { doc, writeBatch, increment, collection, serverTimestamp, updateDoc, getDoc } from "firebase/firestore";
import AuditService, { AUDIT_MODULES, AUDIT_ACTIONS } from "./audit_service";
import XpService, { XP_ACTION_TYPES } from "./xp_service";
import GamificationEngineService from "./gamification_engine";
import { COLLECTIONS } from "../services/collections";
import { TRIAL_ATTENDANCE_STATUS } from "../constants/enrollments";

/**
 * AttendanceService
 * 
 * Responsável por salvar o controle de presença de uma sessão.
 * Opera de forma desnormalizada, atualizando em lote (Batch):
 *   1. Session.attendances  → Array consolidado de presenças na sessão
 *   2. Member.attendanceStats → Contadores individuais do aluno
 *   3. Member/attendanceLogs  → Subcoleção com histórico dia a dia
 *   4. Analytics global       → Contadores mensais da academia
 *
 * STATUS no Firestore:
 *   'ATTENDED'  → Presente
 *   'NO_SHOW'   → Ausente (faltou)
 *   'BOOKED'    → Agendado (chamada não realizada)
 */
const AttendanceService = {
    /**
     * @param {string} idTenant
     * @param {object} session - Sessão do Firestore ({ id, sessionDate, idClass, activityName, employeeName, startTime, ... })
     * @param {Array} membersState - Estado dos alunos vindo da UI ({ id, name, photo, status, oldStatus, justification, enrollmentType, idEnrollment })
     * @param {object} currentUser - Usuário logado ({ uid })
     * @param {object} [gamificationSettings] - Configurações de gamificação do tenant (opcional)
     */
    saveSessionAttendance: async (idTenant, session, membersState, currentUser, gamificationSettings = null, substitutionData = null) => {
        if (!idTenant) throw new Error("idTenant é obrigatório.");
        if (!session?.id) throw new Error("session.id é obrigatório.");
        if (!session.sessionDate) throw new Error("session.sessionDate é obrigatório.");
        if (!currentUser?.uid) throw new Error("currentUser.uid é obrigatório.");

        const batch = writeBatch(db);
        const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, session.id);
        const { id: idSession, sessionDate } = session;

        // -- Mapeamento Frontend → Backend --
        const toBackendStatus = (frontendStatus) => {
            if (frontendStatus === "present") return "ATTENDED";
            if (frontendStatus === "absent") return "NO_SHOW";
            return "BOOKED";
        };

        // ─── 1. Array de attendances na própria Session ───
        const finalAttendances = membersState.map(member => ({
            idMember: member.id,
            memberName: member.name,
            memberEmail: member.email || "",
            memberPhone: member.phone || "",
            memberPhotoUrl: member.photo || null,
            status: toBackendStatus(member.status),
            justification: member.justification || "",
            type: member.enrollmentType,
            idEnrollment: member.idEnrollment || null,
        }));

        const sessionPayload = {
            attendances: finalAttendances,
            attendanceUpdatedBy: currentUser.uid,
            attendanceUpdatedAt: serverTimestamp(),
            attendanceCompleted: true,
        };

        if (substitutionData && substitutionData.idTeacher) {
            sessionPayload.idEmployee = substitutionData.idTeacher;
            sessionPayload.employeeName = substitutionData.teacherName;
            sessionPayload.employeeEmail = substitutionData.teacherEmail || "";
            sessionPayload.employeePhone = substitutionData.teacherPhone || "";
            sessionPayload.employeePhotoUrl = substitutionData.teacherPhotoUrl || null;
            sessionPayload.idOriginalEmployee = session.idOriginalEmployee || session.idEmployee;
            sessionPayload.originalEmployeeName = session.originalEmployeeName || session.employeeName;
            sessionPayload.originalEmployeePhotoUrl = session.originalEmployeePhotoUrl || session.employeePhotoUrl || null;
            sessionPayload.isSubstitution = true;
        }

        batch.update(sessionRef, sessionPayload);

        // ─── 2 e 3. Atualizar cada Member + Gerar Log individual ───
        let totalNewPresents = 0;
        let totalNewAbsents = 0;
        const membersNewlyPresent = [];

        // Tracking para débito/estorno de pacotes de aulas
        const classPackageDebits = [];  // [{idEnrollment}] → debitar 1 aula
        const classPackageRefunds = []; // [{idEnrollment}] → devolver 1 aula

        for (const member of membersState) {
            if (!member.id) continue;

            const isPresent = member.status === "present";
            const isAbsent = member.status === "absent";
            const wasPresent = member.oldStatus === "ATTENDED";
            const wasAbsent = member.oldStatus === "NO_SHOW";

            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, member.id);
            const updates = {};

            if (isPresent && !wasPresent) {
                updates["attendanceStats.presentCount"] = increment(1);
                updates["attendanceStats.lastAttended"] = sessionDate;
                updates["attendanceStats.consecutiveAbsences"] = 0;

                if (wasAbsent) {
                    updates["attendanceStats.absentCount"] = increment(-1);
                    totalNewAbsents -= 1;
                }
                totalNewPresents += 1;
                membersNewlyPresent.push(member.id);

                // ── XP por presença ──
                const xpPerPresence = gamificationSettings?.memberAttendancePresent || 0;
                if (xpPerPresence > 0) {
                    XpService.addXpInBatch(batch, idTenant, {
                        idMember: member.id,
                        xpAmount: xpPerPresence,
                        actionType: XP_ACTION_TYPES.CLASS_ATTENDANCE,
                        sourceId: session.id,
                        sourceName: session.activityName || "Aula",
                        user: { uid: currentUser.uid, name: currentUser.displayName || "Sistema" }
                    });
                }

                // Tracking para pacote de aulas: marcar para débito
                if (member.idEnrollment) {
                    classPackageDebits.push({ idEnrollment: member.idEnrollment });
                }
            } else if (isAbsent && !wasAbsent) {
                updates["attendanceStats.absentCount"] = increment(1);
                updates["attendanceStats.lastAbsence"] = sessionDate;
                updates["attendanceStats.consecutiveAbsences"] = increment(1);

                if (wasPresent) {
                    updates["attendanceStats.presentCount"] = increment(-1);
                    totalNewPresents -= 1;
                }
                totalNewAbsents += 1;

                // Tracking para pacote de aulas: estornar se era presente
                if (wasPresent && member.idEnrollment) {
                    classPackageRefunds.push({ idEnrollment: member.idEnrollment });
                }
            }

            if (Object.keys(updates).length > 0) {
                batch.update(memberRef, updates);
            }

            // Log individual (só quando status mudou)
            const statusChanged = (isPresent && !wasPresent) || (isAbsent && !wasAbsent);
            if (statusChanged) {
                const historyRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS, member.id, COLLECTIONS.ATTENDANCE_LOGS));
                batch.set(historyRef, {
                    idSession,
                    idClass: session.idClass,
                    // Dados do membro (desnormalizados)
                    idMember: member.id,
                    memberName: member.name || "",
                    memberEmail: member.email || "",
                    memberPhone: member.phone || "",
                    memberPhotoUrl: member.photo || null,
                    // Dados do professor (desnormalizados)
                    idEmployee: session.idEmployee || "",
                    employeeName: session.employeeName || "",
                    employeePhotoUrl: session.employeePhotoUrl || null,
                    activityName: session.activityName,
                    date: sessionDate,
                    time: session.startTime,
                    status: isPresent ? "ATTENDED" : "NO_SHOW",
                    justification: member.justification || "",
                    createdAt: serverTimestamp(),
                    performedBy: currentUser.uid,
                });
            }

            // Sync presença da experimental para o enrollment (campo separado)
            if (member.idEnrollment && (member.enrollmentType === "TRIAL" || member.enrollmentType === "trial")) {
                const enrollmentRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS), member.idEnrollment);
                let attendanceStatus = TRIAL_ATTENDANCE_STATUS.PENDING;
                if (isPresent) attendanceStatus = TRIAL_ATTENDANCE_STATUS.PRESENT;
                if (isAbsent) attendanceStatus = TRIAL_ATTENDANCE_STATUS.ABSENT;
                
                batch.update(enrollmentRef, { 
                    attendanceStatus,
                    updatedAt: serverTimestamp() 
                });
            }
        }

        // ─── 4. Analytics Globais (Mês) ───
        if (totalNewPresents !== 0 || totalNewAbsents !== 0) {
            const yearMonth = sessionDate.substring(0, 7).replace("-", "_"); // "2026-03" → "2026_03"
            const analyticsRef = doc(db, "tenants", idTenant, "analytics", `attendance_${yearMonth}`);

            batch.set(analyticsRef, {
                totalPresents: increment(totalNewPresents),
                totalAbsents: increment(totalNewAbsents),
                lastUpdatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // ─── Commit do Batch ───
        await batch.commit();

        // ─── 5. Débito/Estorno de Pacote de Aulas (pós-batch) ───
        await AttendanceService._processClassPackageCredits(idTenant, classPackageDebits, classPackageRefunds);

        // ─── Auditoria ───
        await AuditService.log({
            idTenant,
            user: currentUser,
            module: AUDIT_MODULES.SESSIONS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: idSession,
            entityName: session.activityName,
            description: `Chamada realizada. Presentes: ${totalNewPresents}, Faltas: ${totalNewAbsents}`,
            details: { presentsRecorded: totalNewPresents, absentsRecorded: totalNewAbsents },
        });

        // ─── Disparo do Motor de Gamificação ───
        if (membersNewlyPresent.length > 0 && gamificationSettings) {
            try {
                await Promise.allSettled(
                    membersNewlyPresent.map(idMember =>
                        GamificationEngineService.evaluateMemberGamification(idTenant, idMember, gamificationSettings, currentUser)
                    )
                );
            } catch (err) {
                console.error("[Gamification] Erro ao disparar engine de gamificação:", err);
            }
        }

        return true;
    },

    /**
     * Processa débitos e estornos de pacotes de aulas após o batch de presença.
     * Busca o enrollment → memberMembership para verificar se é tipo "classes".
     * Debita ou devolve 1 crédito por presença/reversão.
     */
    _processClassPackageCredits: async (idTenant, debits, refunds) => {
        if (debits.length === 0 && refunds.length === 0) return;

        const processEntry = async (entry, delta) => {
            try {
                const enrollmentRef = doc(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS, entry.idEnrollment);
                const enrollmentSnap = await getDoc(enrollmentRef);
                if (!enrollmentSnap.exists()) return;

                const enrollment = enrollmentSnap.data();
                
                // Se type='class-package', o crédito já foi debitado na matrícula avulsa.
                if (enrollment.type === 'class-package') return;

                const idMembership = enrollment.idMembership;
                if (!idMembership) return;

                const mmRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, idMembership);
                const mmSnap = await getDoc(mmRef);
                if (!mmSnap.exists()) return;

                const mm = mmSnap.data();
                if (mm.durationType !== 'classes') return;

                const updates = {
                    remainingClasses: increment(delta),
                    updatedAt: serverTimestamp()
                };

                // Se está debitando e o saldo atual chegará a 0, expirar o contrato
                if (delta < 0) {
                    const currentRemaining = mm.remainingClasses || 0;
                    if (currentRemaining + delta <= 0) {
                        updates.status = 'expired';
                    }
                }

                await updateDoc(mmRef, updates);
            } catch (err) {
                console.error('[AttendanceService] Erro ao processar crédito de pacote:', err);
            }
        };

        await Promise.allSettled([
            ...debits.map(entry => processEntry(entry, -1)),
            ...refunds.map(entry => processEntry(entry, +1))
        ]);
    },
    /**
     * Cancela uma sessão
     */
    cancelSession: async (idTenant, session, currentUser, canceledReasonType = "admin") => {
        if (!idTenant || !session?.id) throw new Error("Parâmetros inválidos.");
        const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, session.id);
        
        await updateDoc(sessionRef, {
            status: "CANCELED",
            canceledReasonType: canceledReasonType,
            attendanceCompleted: true,
            attendanceUpdatedBy: currentUser.uid,
            attendanceUpdatedAt: serverTimestamp(),
            canceledAt: serverTimestamp(),
            canceledBy: currentUser.uid
        });

        // Audit Log
        AuditService.log({
            idTenant,
            user: currentUser,
            module: AUDIT_MODULES.SESSIONS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: session.id,
            entityName: session.activityName,
            description: `Cancelou sessão de ${session.activityName || "Atividade"} em ${session.sessionDate || ""} às ${session.startTime || ""}`,
            details: { status: "CANCELED", motivo: canceledReasonType, professor: session.employeeName || "-" }
        }).catch(err => console.error("Erro no audit:", err));
    }
};

export default AttendanceService;
